/**
 * Edge Filter Definition
 *
 * Filters nodes by their relationships/edges (e.g., "has Ragam", "has Composer X")
 * Only shows edges that are valid for the current node type being filtered.
 *
 * Supports:
 * - Has/NotHas: Simple existence checks
 * - HasSpecific: Filter by specific target node(s) (e.g., "has Ragam: Kalyani")
 * - HasCount: Filter by edge count (e.g., "has more than 2 tracks")
 */

import { GraphDataNode, NodeTypeId } from '../../types/graph.types';
import {
  FilterDefinition,
  FilterOperator,
  FilterValueType,
  FilterSuggestion,
  FilterValue,
  EdgeFilterValue,
} from '../types';
import { NodeTypeDefinitions } from '../../config/nodeTypes';
import { EdgeFilterConfig } from '../components/EdgeFilterConfig';

/**
 * Edge display labels (for edges that need custom labels)
 */
const edgeLabels: Record<string, string> = {
  ragam: 'Ragam',
  talam: 'Talam',
  composer: 'Composer',
  deity: 'Deity',
  lyrics: 'Lyrics',
  audible: 'Audible',
  meaning: 'Meaning',
  compositionLanguage: 'Language',
  bb: 'Bhajan Babies',
  pitch: 'Pitch',
  album: 'Album',
  track: 'Track',
  collection: 'Collection',
  bhajan: 'Bhajan',
  rashi: 'Rashi',
  star: 'Star',
  bodyPart: 'Body Part',
  chakra: 'Chakra',
  rashiElem: 'Rashi Element',
  rashiGem: 'Rashi Gem',
  rashiColor: 'Rashi Color',
  rashiTree: 'Rashi Tree',
  chakraElem: 'Chakra Element',
  chakraGem: 'Chakra Gem',
  chakraColor: 'Chakra Color',
  element: 'Element',
  gem: 'Gem',
  color: 'Color',
  tree: 'Tree',
  place: 'Place',
  concert: 'Concert',
  concertTrack: 'Concert Track',
  thithi: 'Thithi',
  westernNotes: 'Western Notes',
};

/**
 * Get label for an edge name
 */
function getEdgeLabel(edgeName: string): string {
  return edgeLabels[edgeName] || edgeName.charAt(0).toUpperCase() + edgeName.slice(1);
}

/**
 * Get edges available for a specific node type
 */
function getEdgesForNodeType(nodeTypeId: NodeTypeId): string[] {
  const nodeTypeDef = NodeTypeDefinitions[nodeTypeId];
  if (!nodeTypeDef) return [];
  return nodeTypeDef.edges.map(e => e.name);
}

/**
 * Detect the node type from a list of nodes
 * Returns undefined if nodes are mixed types or empty
 */
function detectNodeType(nodes: GraphDataNode[]): NodeTypeId | undefined {
  if (nodes.length === 0) return undefined;

  const firstType = nodes[0].nodeTypeId;
  const allSameType = nodes.every(n => n.nodeTypeId === firstType);

  return allSameType ? firstType : undefined;
}

/**
 * Get edge value(s) from node by name. The serverless API returns edges as an
 * array of {name, nodeId} pairs (canonical form for jnana-web). Admin used a
 * Record<string, number | number[]> map; this helper handles either shape so
 * the rest of the filter code is data-shape-agnostic.
 */
function getEdgeValue(node: GraphDataNode, edgeName: string): number | number[] | undefined {
  const raw: Array<{ name: string; nodeId: number }> | Record<string, number | number[]> | undefined =
    node.edges ?? (node.data?.edges as Record<string, number | number[]> | undefined);
  if (!raw) return undefined;
  if (Array.isArray(raw)) {
    const matches = raw.filter((e) => e && e.name === edgeName).map((e) => e.nodeId as number);
    if (matches.length === 0) return undefined;
    return matches.length === 1 ? matches[0] : matches;
  }
  // object-map form
  return raw[edgeName];
}

/**
 * Get edge count for a node
 */
function getEdgeCount(node: GraphDataNode, edgeName: string): number {
  const edgeValue = getEdgeValue(node, edgeName);
  if (edgeValue === undefined || edgeValue === null) return 0;
  if (Array.isArray(edgeValue)) return edgeValue.length;
  return 1;
}

/**
 * Check if a node has a specific edge (with optional target node IDs)
 */
function nodeHasEdge(
  node: GraphDataNode,
  edgeName: string,
  targetNodeIds?: number[]
): boolean {
  const edgeValue = getEdgeValue(node, edgeName);
  if (edgeValue === undefined || edgeValue === null) {
    return false;
  }

  // If no specific targets are required, just check existence
  if (!targetNodeIds || targetNodeIds.length === 0) {
    if (Array.isArray(edgeValue)) {
      return edgeValue.length > 0;
    }
    return true;
  }

  // Check for specific target nodes (ANY of them)
  if (Array.isArray(edgeValue)) {
    return targetNodeIds.some(id => edgeValue.includes(id));
  }
  return targetNodeIds.includes(edgeValue);
}

/**
 * Check edge count against comparison
 */
function checkEdgeCount(
  node: GraphDataNode,
  edgeName: string,
  comparison: EdgeFilterValue['countComparison']
): boolean {
  if (!comparison) return true;

  const count = getEdgeCount(node, edgeName);
  const { operator, value } = comparison;

  switch (operator) {
    case 'equals':
      return count === value;
    case 'greaterThan':
      return count > value;
    case 'lessThan':
      return count < value;
    case 'greaterThanOrEqual':
      return count >= value;
    case 'lessThanOrEqual':
      return count <= value;
    default:
      return true;
  }
}

/**
 * Parse filter value to EdgeFilterValue
 */
function parseEdgeFilterValue(value: FilterValue): EdgeFilterValue {
  if (typeof value === 'object' && value !== null && 'edgeName' in value) {
    return value as EdgeFilterValue;
  }
  // Legacy: plain string edge name
  return { edgeName: String(value) };
}

/**
 * Edge Filter Definition
 */
export const edgeFilter: FilterDefinition = {
  id: 'edge',
  label: 'Has Relationship',
  shortLabel: 'Has',
  icon: 'LinkIcon',
  valueType: FilterValueType.EdgeName,
  supportedOperators: [
    FilterOperator.Has,
    FilterOperator.NotHas,
    FilterOperator.HasSpecific,
    FilterOperator.HasCount,
  ],
  defaultOperator: FilterOperator.Has,
  placeholder: 'Select relationship...',
  ConfigComponent: EdgeFilterConfig,

  suggestMatch(query: string, nodes: GraphDataNode[]): FilterSuggestion[] {
    const normalizedQuery = query.toLowerCase().trim();
    if (!normalizedQuery) {
      return [];
    }

    const suggestions: FilterSuggestion[] = [];

    // Detect the current node type from the nodes
    const nodeTypeId = detectNodeType(nodes);

    // Get edges valid for this node type (or empty if no consistent type)
    const validEdges = nodeTypeId ? getEdgesForNodeType(nodeTypeId) : [];

    // If no valid edges for this type, don't suggest edge filters
    if (validEdges.length === 0) {
      return [];
    }

    // Check for "has" keyword
    const hasMatch = normalizedQuery.startsWith('has ');
    const searchTerm = hasMatch ? normalizedQuery.slice(4) : normalizedQuery;

    // Match against valid edge names only
    for (const edgeName of validEdges) {
      const label = getEdgeLabel(edgeName);
      const normalizedLabel = label.toLowerCase();
      const normalizedEdgeName = edgeName.toLowerCase();

      let matchScore = 0;

      // Check for matches
      if (normalizedLabel === searchTerm || normalizedEdgeName === searchTerm) {
        matchScore = 100;
      } else if (normalizedLabel.startsWith(searchTerm) || normalizedEdgeName.startsWith(searchTerm)) {
        matchScore = 80;
      } else if (normalizedLabel.includes(searchTerm) || normalizedEdgeName.includes(searchTerm)) {
        matchScore = 50;
      }

      if (matchScore > 0 || hasMatch) {
        // Count how many nodes have this edge
        const nodesWithEdge = nodes.filter((n) => nodeHasEdge(n, edgeName)).length;
        const nodesWithoutEdge = nodes.length - nodesWithEdge;

        // "Has" suggestion (simple existence)
        if (nodesWithEdge > 0) {
          suggestions.push({
            filterId: 'edge',
            label: `Has: ${label}`,
            description: `${nodesWithEdge} node${nodesWithEdge !== 1 ? 's' : ''} have this relationship`,
            prefilledOperator: FilterOperator.Has,
            prefilledValue: { edgeName } as EdgeFilterValue,
            needsConfiguration: false,
            score: matchScore + (hasMatch ? 20 : 0),
          });

          // "Has specific..." suggestion (opens config to select specific nodes)
          suggestions.push({
            filterId: 'edge',
            label: `Has specific ${label}...`,
            description: `Select specific ${label.toLowerCase()}(s) to filter by`,
            prefilledOperator: FilterOperator.HasSpecific,
            prefilledValue: { edgeName, targetNodeIds: [] } as EdgeFilterValue,
            needsConfiguration: true,
            score: matchScore + (hasMatch ? 15 : -5),
          });
        }

        // "Does not have" suggestion
        if (nodesWithoutEdge > 0 && matchScore > 60) {
          suggestions.push({
            filterId: 'edge',
            label: `Missing: ${label}`,
            description: `${nodesWithoutEdge} node${nodesWithoutEdge !== 1 ? 's' : ''} don't have this`,
            prefilledOperator: FilterOperator.NotHas,
            prefilledValue: { edgeName } as EdgeFilterValue,
            needsConfiguration: false,
            score: matchScore - 10,
          });
        }
      }
    }

    // Generic "has" suggestion if just typing "has"
    if (normalizedQuery === 'has' || normalizedQuery === 'has ') {
      const firstEdge = validEdges[0];
      if (firstEdge) {
        suggestions.push({
          filterId: 'edge',
          label: 'Has: ...',
          description: 'Filter by relationship existence',
          prefilledOperator: FilterOperator.Has,
          prefilledValue: { edgeName: firstEdge } as EdgeFilterValue,
          needsConfiguration: true,
          score: 30,
        });
      }
    }

    // "Missing" keyword suggestions
    if (normalizedQuery.startsWith('missing') || normalizedQuery.startsWith('no ')) {
      const missingTerm = normalizedQuery.replace(/^(missing|no)\s*/, '');

      for (const edgeName of validEdges) {
        const label = getEdgeLabel(edgeName);
        const normalizedLabel = label.toLowerCase();
        if (
          !missingTerm ||
          normalizedLabel.includes(missingTerm) ||
          edgeName.toLowerCase().includes(missingTerm)
        ) {
          const nodesWithoutEdge = nodes.filter((n) => !nodeHasEdge(n, edgeName)).length;

          if (nodesWithoutEdge > 0) {
            suggestions.push({
              filterId: 'edge',
              label: `Missing: ${label}`,
              description: `${nodesWithoutEdge} node${nodesWithoutEdge !== 1 ? 's' : ''} don't have this`,
              prefilledOperator: FilterOperator.NotHas,
              prefilledValue: { edgeName } as EdgeFilterValue,
              needsConfiguration: false,
              score: 70,
            });
          }
        }
      }
    }

    // "Count" keyword suggestions
    if (normalizedQuery.includes('count') || normalizedQuery.includes('more than') || normalizedQuery.includes('at least')) {
      for (const edgeName of validEdges) {
        const label = getEdgeLabel(edgeName);
        suggestions.push({
          filterId: 'edge',
          label: `${label} count...`,
          description: `Filter by number of ${label.toLowerCase()} relationships`,
          prefilledOperator: FilterOperator.HasCount,
          prefilledValue: { edgeName, countComparison: { operator: 'greaterThanOrEqual', value: 1 } } as EdgeFilterValue,
          needsConfiguration: true,
          score: 60,
        });
      }
    }

    return suggestions.sort((a, b) => b.score - a.score);
  },

  execute(node: GraphDataNode, operator: FilterOperator, value: FilterValue): boolean {
    const edgeValue = parseEdgeFilterValue(value);
    const { edgeName, targetNodeIds, countComparison } = edgeValue;

    switch (operator) {
      case FilterOperator.Has:
        return nodeHasEdge(node, edgeName);

      case FilterOperator.NotHas:
        return !nodeHasEdge(node, edgeName);

      case FilterOperator.HasSpecific:
        // Must have edge with at least one of the specified target nodes
        return nodeHasEdge(node, edgeName, targetNodeIds);

      case FilterOperator.HasCount:
        // Check edge count against comparison
        return checkEdgeCount(node, edgeName, countComparison);

      default:
        return true;
    }
  },

  getDisplayValue(value: FilterValue): string {
    const edgeValue = parseEdgeFilterValue(value);
    const label = getEdgeLabel(edgeValue.edgeName);

    // Handle count comparison
    if (edgeValue.countComparison) {
      const countOpLabels: Record<string, string> = {
        equals: '=',
        greaterThan: '>',
        lessThan: '<',
        greaterThanOrEqual: '>=',
        lessThanOrEqual: '<=',
      };
      const opSymbol = countOpLabels[edgeValue.countComparison.operator] || '';
      return `${label} ${opSymbol} ${edgeValue.countComparison.value}`;
    }

    // Handle specific target nodes
    if (edgeValue.targetNodeIds && edgeValue.targetNodeIds.length > 0) {
      if (edgeValue.targetNodeIds.length === 1) {
        return `${label} #${edgeValue.targetNodeIds[0]}`;
      }
      return `${label} (${edgeValue.targetNodeIds.length} selected)`;
    }

    return label;
  },
};
